/**
 * Production-grade skeleton loaders for polished loading states.
 */

export function Skeleton({ className = '' }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-slate-200/80 dark:bg-muted/60 ${className}`}
      aria-hidden="true"
    />
  );
}

export function SkeletonText({ lines = 3, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`}
        />
      ))}
    </div>
  );
}

export function SkeletonKpiCard() {
  return (
    <div className="rounded-2xl border border-border/80 dark:border-border/80 bg-white/80 dark:bg-muted/40 p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-3 flex-1">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-12 w-12 rounded-xl shrink-0" />
      </div>
    </div>
  );
}

export function SkeletonChart({ className = 'h-64' }) {
  return (
    <div className={`rounded-2xl border border-border/80 dark:border-border/80 bg-white/80 dark:bg-muted/40 p-5 ${className}`}>
      <Skeleton className="h-4 w-40 mb-4" />
      <Skeleton className="h-full min-h-[12rem] w-full rounded-xl" />
    </div>
  );
}

export function SkeletonTable({ rows = 5 }) {
  return (
    <div className="rounded-2xl border border-border/80 dark:border-border/80 overflow-hidden">
      <div className="p-4 border-b border-border/80 dark:border-border/80">
        <Skeleton className="h-4 w-48" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-4 border-b border-border dark:border-slate-800 last:border-0">
          <Skeleton className="h-10 w-10 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-20 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

export function PageSkeleton({ variant = 'dashboard' }) {
  if (variant === 'table') {
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <Skeleton className="h-10 w-64" />
        <SkeletonTable rows={6} />
      </div>
    );
  }

  if (variant === 'cards') {
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonKpiCard key={i} />)}
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-border/80 dark:border-border/80 p-5 space-y-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-8 w-32 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>
        <Skeleton className="h-10 w-32 rounded-xl" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <SkeletonKpiCard key={i} />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SkeletonChart className="lg:col-span-2 h-72" />
        <SkeletonChart className="h-72" />
      </div>
    </div>
  );
}

export default Skeleton;
